import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { DatePicker, Flex, Input, InputNumber, Select, Tag } from 'antd';

import classnames from 'classnames';
import dayjs from 'dayjs';
import { isEmail } from 'validator';

import { capitalize } from '../../../helpers/common';
import { disablePastTime } from '../../../helpers/date';
import { getEventNullState, validateEvent } from '../../../helpers/event';

import { DATE_FORMAT } from '../../../constants/common';
import {
  CUSTOM_RECURRING_UNITS,
  DEFAULT_EVENT_DURATION,
  EVENT_MODAL_MODE,
  MINUTE_STEP,
  MIN_EVENT_DURATION,
  RECURRING_TYPES,
  RELATION,
  RELATION_SUBTYPE,
  RELATION_TYPES
} from '../../../constants/event';
import messages from '../../../constants/messages';

import { useModal } from '../../../hooks/useModal';

import {
  acceptEvent,
  createNewEvent,
  declineEvent,
  deleteEvent,
  getAllEvents,
  getEventById,
  sendRelationQuery,
  updateEvent
} from '../../../redux/actions/event';

import AppModal from '../../../components/AppModal';

import styles from '../styles.module.scss';

import ConfirmationModal from '../../Modals/ConfirmationModal';
import { errorToastHandler } from '../../action_notifier';
import EventDetails from './EventDetails';
import EventModalFooter from './EventModalFooter';

const EventModal = ({ isOpen, mode = EVENT_MODAL_MODE.CREATE, startDate, endDate, onCancel, onSubmit }) => {
  const EVENT_NULL_STATE = getEventNullState();
  const dispatch = useDispatch();

  const eventState = useSelector(state => state.event);
  const userState = useSelector(state => state.user);

  const [confirmationModalVisible, openConfirmationModal, closeConfirmationModal] = useModal();
  const [deleteRecurringTasksModalVisible, openDeleteRecurringTasksModal, closeDeleteRecurringTasksModal] = useModal();

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [externalEmails, setExternalEmails] = useState([]);
  const [modalMode, setModalMode] = useState(mode);
  const [newEvent, setNewEvent] = useState(EVENT_NULL_STATE);
  const [relations, setRelations] = useState([]);
  const [externalEmailInputValue, setExternalEmailInputValue] = useState('');

  const defaultUserId = useMemo(
    () => userState?.assignableUsers?.find(user => user.email === userState.user?.email)?.id,
    [userState]
  );

  useEffect(() => {
    if (!userState?.assignableUsers?.length) return;

    setAssignedUsers([defaultUserId]);
  }, [userState]);

  useEffect(() => {
    if (mode !== EVENT_MODAL_MODE.CREATE && eventState.currentEvent.id && isOpen) {
      dispatch(getEventById(eventState.currentEvent.id));
    }
  }, [isOpen]);

  const getRelationOptions = relationType => {
    dispatch(sendRelationQuery(relationType)).then(data => {
      if (relationType === RELATION.PASTURE) {
        const filteredPastures = data.reduce((acc, pasture) => (pasture.is_master ? acc : acc.concat(pasture)), []);
        setRelations(filteredPastures);
      } else {
        setRelations(Array.isArray(data) ? data : data.records);
      }
    });
  };

  const onCancelClick = () => {
    closeConfirmationModal();
    setNewEvent(EVENT_NULL_STATE);

    if (eventState?.currentEvent?.is_owner) {
      setModalMode(mode);
    }

    setAssignedUsers([defaultUserId]);
    onCancel();
  };

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    setNewEvent(getEventNullState(startDate, endDate));
  }, [startDate, endDate]);

  useEffect(() => {
    setNewEvent(getEventNullState(startDate, endDate));
  }, [isOpen]);

  useEffect(() => {
    if (newEvent.relationType) {
      getRelationOptions(newEvent.relationType);
    }
  }, [newEvent.relationType]);

  const onChange = (value, field) => {
    const formattedValue = value ?? EVENT_NULL_STATE[field];

    if (field === 'startDate') {
      const endDate = dayjs(formattedValue).add(DEFAULT_EVENT_DURATION.VALUE, DEFAULT_EVENT_DURATION.UNIT);

      if (dayjs(endDate).isValid()) {
        setNewEvent(prevState => ({ ...prevState, endDate }));
      }
    }

    if (field === 'relationType') {
      setNewEvent(prevState => ({
        ...prevState,
        relationType: formattedValue,
        relations:
          value === eventState?.currentEvent?.relationType && mode !== EVENT_MODAL_MODE.CREATE
            ? eventState?.currentEvent?.relations
            : []
      }));

      return;
    }

    setNewEvent(prevState => ({ ...prevState, [field]: formattedValue }));
  };

  const onChangeRelation = values => {
    setNewEvent(prevState => ({ ...prevState, relations: Array.isArray(values) ? values : [values] }));
  };

  const onCreateClick = () => {
    const payload = {
      ...newEvent,
      relationIds: newEvent.relations,
      userIds: assignedUsers,
      externalEmails
    };

    const error = validateEvent(payload);

    if (error) {
      errorToastHandler(error);
    } else {
      dispatch(createNewEvent(payload)).then(res => {
        if (res) {
          dispatch(getAllEvents());
          setAssignedUsers([defaultUserId]);
          setExternalEmails([]);
          onSubmit?.();
        }
      });
    }
  };

  const onDeleteSubmit = async () => {
    await dispatch(deleteEvent(eventState.currentEvent.id));
    await dispatch(getAllEvents(eventState.queryParams));

    closeConfirmationModal();
    setNewEvent(EVENT_NULL_STATE);
    onCancel();
  };

  const onDeleteRecurringEventsSubmit = () => {
    dispatch(deleteEvent(eventState.currentEvent.id, { count: 'all' })).then(() => {
      dispatch(getAllEvents(eventState.queryParams));
      closeDeleteRecurringTasksModal();
      setNewEvent(EVENT_NULL_STATE);
      onCancel();
    });
  };

  const onEditClick = () => {
    const event = {
      ...eventState.currentEvent,
      startDate: eventState.currentEvent.start_at ? dayjs(eventState.currentEvent.start_at).format() : null,
      endDate: eventState.currentEvent.end_at ? dayjs(eventState.currentEvent.end_at).format() : null,
      remindDate: eventState.currentEvent.remind_at ? dayjs(eventState.currentEvent.remind_at).format() : null
    };

    setNewEvent({
      ...event,
      relations: event?.relations?.map(relation => ({
        value: relation.id,
        label: relation.name ? `${relation.identifier} ${relation.name}` : `${relation.identifier}`
      }))
    });
    setAssignedUsers(event?.userIds);
    setExternalEmails(event?.external_emails);
    setModalMode(EVENT_MODAL_MODE.UPDATE);
  };

  const onUpdateClick = () => {
    const relations_with_subtypes =
      newEvent.relations?.at(0) === eventState.currentEvent?.relations?.map(relation => relation.id)?.at(0)
        ? eventState.currentEvent?.relations_with_subtypes
        : [];
    const rotationEvent = eventState.currentEvent?.relations_with_subtypes?.find(
      r => r.relation_subtype === RELATION_SUBTYPE.ROTATION
    );
    const formattedEvent = {
      description: newEvent.description,
      endDate: dayjs(newEvent.endDate).format(),
      recurringEndDate: newEvent.recurringEndDate || undefined,
      recurringNumber: newEvent.recurringNumber || undefined,
      recurringType: newEvent.recurringType || undefined,
      recurringUnit: newEvent.recurringUnit || undefined,
      relationIds: newEvent.relations,
      relationType: newEvent.relationType || null,
      relationSubtypes: rotationEvent?.relation_subtype ?? null,
      remindDate: newEvent.remindDate ? dayjs(newEvent.remindDate).format() : null,
      startDate: dayjs(newEvent.startDate).format(),
      title: newEvent.title,
      userIds: [...assignedUsers],
      externalEmails,
      relations_with_subtypes
    };

    const error = validateEvent(formattedEvent);

    if (error) {
      errorToastHandler(error);
    } else {
      dispatch(updateEvent(newEvent.id, formattedEvent)).then(res => {
        if (res) {
          dispatch(getAllEvents(eventState.queryParams));
          setNewEvent(EVENT_NULL_STATE);
          setAssignedUsers([]);
          setExternalEmails([]);
          setModalMode(EVENT_MODAL_MODE.VIEW);
          onCancel();
        }
      });
    }
  };

  const onDeclineClick = () => {
    dispatch(declineEvent(eventState.currentEvent.id)).then(res => {
      if (res) {
        dispatch(getAllEvents(eventState.queryParams));
        setNewEvent(EVENT_NULL_STATE);
        setModalMode(EVENT_MODAL_MODE.VIEW);
        onCancel();
      }
    });
  };

  const onAcceptClick = () => {
    dispatch(acceptEvent(eventState.currentEvent.id)).then(res => {
      if (res) {
        dispatch(getAllEvents(eventState.queryParams));
        setNewEvent(EVENT_NULL_STATE);
        setModalMode(EVENT_MODAL_MODE.VIEW);
        onCancel();
      }
    });
  };

  function setUsers(ids) {
    setAssignedUsers(ids);
  }

  const handleExternalEmailInputConfirm = () => {
    if (isEmail(externalEmailInputValue) && externalEmails.indexOf(externalEmailInputValue) === -1) {
      setExternalEmails([...externalEmails, externalEmailInputValue]);
    }

    setExternalEmailInputValue('');
  };

  const handleExternalEmailInputChange = e => {
    setExternalEmailInputValue(e.target.value);
  };

  const handleRemoveExternalEmail = email => {
    const newExternalEmails = externalEmails.filter(externalEmail => externalEmail !== email);

    setExternalEmails(newExternalEmails);
  };

  const minDate = dayjs(newEvent?.startDate).add(MIN_EVENT_DURATION.VALUE, MIN_EVENT_DURATION.UNIT);
  const rotationEvent = eventState?.currentEvent?.relations_with_subtypes?.find(
    r => r.relation_subtype === RELATION_SUBTYPE.ROTATION
  );
  const isChangeRelationAllowed = !rotationEvent || mode === EVENT_MODAL_MODE.CREATE;
  const relationOptions = useMemo(
    () =>
      relations?.map(option => ({
        value: option.id,
        label: option.name ? `${option.identifier} ${option.name}` : `${option.identifier}`
      })),
    [relations]
  );

  return (
    <div className="event-modal-component">
      <ConfirmationModal
        description={messages.ARCHIVE_CONFIRMATION('event')}
        isOpen={confirmationModalVisible}
        title={'Delete event'}
        onCancel={closeConfirmationModal}
        onSubmit={onDeleteSubmit}
      />
      <ConfirmationModal
        description="This will permanently delete this event and all its future recurring instances. This action cannot be undone."
        title="Delete all recurring events"
        isOpen={deleteRecurringTasksModalVisible}
        onCancel={closeDeleteRecurringTasksModal}
        onSubmit={onDeleteRecurringEventsSubmit}
      />
      <AppModal
        isOpen={isOpen}
        title={`${capitalize(modalMode)} ${(modalMode === 'create' && 'new') || ''} event`}
        footer={
          <EventModalFooter
            currentEvent={eventState?.currentEvent}
            modalMode={modalMode}
            newEvent={newEvent}
            onAcceptClick={onAcceptClick}
            onCancelClick={onCancelClick}
            onCreateClick={onCreateClick}
            onDeclineClick={onDeclineClick}
            onDeleteClick={openConfirmationModal}
            onDeleteRecurringEventsClick={openDeleteRecurringTasksModal}
            onEditClick={onEditClick}
            onUpdateClick={onUpdateClick}
          />
        }
        handleCancel={onCancelClick}>
        <>
          {modalMode === EVENT_MODAL_MODE.CREATE || modalMode === EVENT_MODAL_MODE.UPDATE ? (
            <Flex className="py-4" vertical={true} gap={10}>
              <Flex vertical={true} gap={5}>
                <label className={classnames(styles.label, 'required-field')} htmlFor="title">
                  Title
                </label>
                <Input id="title" value={newEvent.title} onChange={e => onChange(e.target.value, 'title')} />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="description">
                  Description
                </label>
                <Input
                  id="description"
                  value={newEvent.description}
                  onChange={e => onChange(e.target.value, 'description')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={classnames(styles.label, 'required-field')} htmlFor="start-date">
                  Start date
                </label>
                <DatePicker
                  id="start-date"
                  className="w-full"
                  variant="outlined"
                  allowClear={false}
                  value={newEvent.startDate ? dayjs(newEvent.startDate) : dayjs()}
                  disabledDate={current => current && current < dayjs().startOf('day')}
                  disabledTime={current => {
                    if (!current) return {};

                    const isToday = current.isSame(dayjs(), 'day');

                    if (!isToday) {
                      return {};
                    }

                    const currentHour = dayjs().hour();
                    const currentMinute = dayjs().minute();

                    return {
                      disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(hour => hour < currentHour),
                      disabledMinutes: selectedHour =>
                        selectedHour === currentHour
                          ? Array.from({ length: 60 }, (_, i) => i).filter(min => min < currentMinute)
                          : []
                    };
                  }}
                  format={DATE_FORMAT.DATETIME}
                  showTime={{ minuteStep: MINUTE_STEP }}
                  onChange={e => onChange(e, 'startDate')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={classnames(styles.label, 'required-field')} htmlFor="end-date">
                  End date
                </label>
                <DatePicker
                  id="end-date"
                  className="w-full"
                  variant="outlined"
                  allowClear={false}
                  value={newEvent.endDate ? dayjs(newEvent.endDate) : dayjs().add(2, 'h')}
                  disabledDate={current => current && current < minDate.startOf('day') && current < dayjs()}
                  disabledTime={current => disablePastTime(current, minDate)}
                  format={DATE_FORMAT.DATETIME}
                  showTime={{ minuteStep: MINUTE_STEP }}
                  onChange={e => onChange(e, 'endDate')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="remind-date">
                  Remind date
                </label>
                <DatePicker
                  id="remind-date"
                  className="w-full"
                  variant="outlined"
                  allowClear={false}
                  defaultValue={newEvent.remindDate ? dayjs(newEvent.remindDate) : undefined}
                  disabledDate={current => current && current < dayjs().startOf('day')}
                  format={DATE_FORMAT.DATETIME}
                  showTime={{ minuteStep: MINUTE_STEP }}
                  onChange={e => onChange(e, 'remindDate')}
                />
              </Flex>

              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="relation">
                  Relation
                </label>
                <Select
                  id="relation"
                  className="w-full"
                  allowClear={true}
                  disabled={!isChangeRelationAllowed}
                  options={RELATION_TYPES.map(type => ({ value: type, label: capitalize(type) }))}
                  value={{ value: newEvent.relationType, label: capitalize(newEvent.relationType) }}
                  onChange={value => onChange(value, 'relationType')}
                />
              </Flex>

              {newEvent.relationType && (
                <Flex vertical={true} gap={5}>
                  <label className={classnames(styles.label, 'text-capitalize')} htmlFor="relation-type">
                    {newEvent.relationType}
                  </label>
                  <Select
                    id="relation-type"
                    className="w-full"
                    mode={(!rotationEvent || mode === EVENT_MODAL_MODE.CREATE) && 'multiple'}
                    optionFilterProp="label"
                    allowClear={true}
                    options={relationOptions}
                    showSearch={true}
                    value={newEvent.relations}
                    onChange={onChangeRelation}
                  />
                </Flex>
              )}

              <Flex vertical={true} gap={5}>
                <label className={styles.label}>Recurring type</label>
                <Select
                  className="w-full"
                  allowClear={true}
                  value={{ label: capitalize(newEvent.recurringType) }}
                  options={RECURRING_TYPES.map(type => ({ value: type, label: capitalize(type) }))}
                  onChange={value => onChange(value, 'recurringType')}
                />
              </Flex>

              {newEvent.recurringType && (
                <>
                  <Flex vertical={true} gap={5}>
                    <label className={classnames(styles.label, 'required-field')} htmlFor="recurring-end-date">
                      Recurring end date
                    </label>
                    <DatePicker
                      id="recurring-end-date"
                      className="w-full"
                      variant="outlined"
                      allowClear={false}
                      defaultValue={newEvent.recurringEndDate ? dayjs(newEvent.recurringEndDate) : undefined}
                      format={DATE_FORMAT.DATETIME}
                      showTime={{ minuteStep: MINUTE_STEP }}
                      onChange={e => onChange(e, 'recurringEndDate')}
                    />
                  </Flex>
                  {newEvent.recurringType === 'custom' && (
                    <Flex vertical={true} gap={5}>
                      <label className={classnames(styles.label, 'required-field')}>Custom Recurrence</label>
                      <Flex className="w-full" gap="small">
                        <InputNumber min={1} className="w-full" onChange={e => onChange(e, 'recurringNumber')} />
                        <Select
                          className="w-full"
                          onChange={value => onChange(value, 'recurringUnit')}
                          options={CUSTOM_RECURRING_UNITS.map(unit => ({
                            value: unit,
                            label: capitalize(unit)
                          }))}
                        />
                      </Flex>
                    </Flex>
                  )}
                </>
              )}

              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="users">
                  Users
                </label>
                <Flex className="w-full" vertical={true}>
                  <Select
                    className="w-full"
                    mode="multiple"
                    id="users"
                    optionFilterProp="label"
                    allowClear={true}
                    value={assignedUsers}
                    options={userState.assignableUsers?.map(user => ({
                      value: user.id,
                      label: user.email,
                      disabled: user.id === defaultUserId
                    }))}
                    showSearch={true}
                    onChange={setUsers}
                  />
                  <Link to="/profile?tab=linked-users">Can&apos;t find the user? Link an account.</Link>
                </Flex>
              </Flex>
              <Flex vertical gap={5}>
                <label className={styles.label} htmlFor="users">
                  Invite emails
                </label>
                <Flex className="w-full" vertical={true}>
                  <Input
                    className="mb-2"
                    value={externalEmailInputValue}
                    onChange={handleExternalEmailInputChange}
                    onBlur={handleExternalEmailInputConfirm}
                    onPressEnter={handleExternalEmailInputConfirm}
                  />
                  <div>
                    {externalEmails.map(email => (
                      <span key={email} style={{ display: 'inline-block' }}>
                        <Tag
                          closable
                          onClose={e => {
                            e.preventDefault();
                            handleRemoveExternalEmail(email);
                          }}>
                          {email}
                        </Tag>
                      </span>
                    ))}
                  </div>
                </Flex>
              </Flex>
            </Flex>
          ) : (
            <EventDetails event={eventState?.currentEvent} />
          )}
        </>
      </AppModal>
    </div>
  );
};

export default memo(EventModal);
